import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Target, Dumbbell, LineChart, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";

const HERO = "https://images.pexels.com/photos/6388985/pexels-photo-6388985.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const features = [
  { icon: Target, title: "Onboarding précis", desc: "Physio, objectif de course, stations faibles, dispo, nutrition et sommeil capturés en quelques minutes." },
  { icon: Dumbbell, title: "Programme généré par IA", desc: "Un plan semaine par semaine jusqu'à ta course : course, force, WOD HYROX, périodisation et taper." },
  { icon: LineChart, title: "Suivi & progression", desc: "Logue tes séances en salle, suis ton pace compromised, tes wall balls et ton poids dans le temps." },
];

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="flex items-center justify-between px-5 md:px-10 py-5 border-b border-border">
        <div className="flex items-center gap-2">
          <Flame className="text-primary" size={26} strokeWidth={2.5} />
          <span className="font-display text-2xl">ROX<span className="text-primary">LAB</span></span>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="nav-pricing-btn" variant="ghost" className="text-sm hidden sm:inline-flex" onClick={() => nav("/pricing")}>Tarifs</Button>
          <Button data-testid="nav-login-btn" variant="ghost" className="text-sm" onClick={() => nav("/auth")}>Connexion</Button>
          <Button data-testid="nav-signup-btn" className="bg-primary hover:bg-primary/90 text-white rounded-md text-sm" onClick={() => nav("/auth?mode=register")}>Commencer</Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="grid lg:grid-cols-2 gap-10 px-5 md:px-10 py-14 md:py-24 max-w-7xl mx-auto items-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">Coaching HYROX · Méthode structurée</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.9]">
              Prépare ta course.<br /><span className="text-primary">Bats ton chrono.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-md">
              La méthodologie de coaching HYROX transformée en programme personnalisé. Entraînement + nutrition, suivi à distance par ton coach.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button data-testid="hero-cta-btn" className="bg-primary hover:bg-primary/90 text-white rounded-md h-14 px-8 text-base font-semibold active:scale-95 transition-transform" onClick={() => nav("/auth?mode=register")}>
                Créer mon programme <ChevronRight size={18} />
              </Button>
              <Button data-testid="hero-login-btn" variant="outline" className="h-14 px-8 text-base rounded-md border-border bg-transparent hover:bg-secondary" onClick={() => nav("/auth")}>
                J'ai déjà un compte
              </Button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative">
            <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full" />
            <img src={HERO} alt="Athlète HYROX" className="relative rounded-lg border border-border object-cover w-full h-[340px] md:h-[480px]" />
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="px-5 md:px-10 py-16 max-w-7xl mx-auto">
        <h2 className="font-display text-3xl md:text-4xl mb-10">La méthode, en 3 temps</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-card border border-border rounded-lg p-6">
              <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center mb-4">
                <f.icon className="text-primary" size={24} />
              </div>
              <h3 className="text-xl font-bold mb-2">{f.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 md:px-10 pb-20">
        <div className="max-w-7xl mx-auto bg-primary rounded-lg p-8 md:p-14 text-center">
          <h2 className="font-display text-4xl md:text-5xl text-white">Prêt à performer ?</h2>
          <p className="text-white/90 mt-3 max-w-lg mx-auto">Renseigne ton profil et reçois ton plan HYROX personnalisé en quelques secondes.</p>
          <Button data-testid="cta-bottom-btn" className="mt-6 bg-black hover:bg-black/80 text-white h-14 px-10 rounded-md text-base font-semibold active:scale-95 transition-transform" onClick={() => nav("/auth?mode=register")}>
            Démarrer maintenant
          </Button>
        </div>
      </section>

      <footer className="border-t border-border px-5 md:px-10 py-6 text-center text-xs text-muted-foreground">
        <p>© {new Date().getFullYear()} Roxlab · Coaching sportif & nutritionnel spécialisé HYROX</p>
        <div className="mt-2 space-x-3">
          <button data-testid="footer-legal-link" onClick={() => nav("/legal")} className="hover:text-foreground">Mentions légales</button>
          <span>·</span>
          <button data-testid="footer-privacy-link" onClick={() => nav("/privacy")} className="hover:text-foreground">Politique de confidentialité</button>
        </div>
      </footer>
    </div>
  );
}
